import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import SearchableSelect from "@/components/SearchableSelect";
import { DownloadSimple, Package, Truck } from "@phosphor-icons/react";

const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const firstOfMonth = () => { const d = new Date(); return iso(new Date(d.getFullYear(), d.getMonth(), 1)); };

export default function PurchaseSummary() {
  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(iso(new Date()));
  const [supplierId, setSupplierId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [data, setData] = useState({ rows: [], total_amount: 0, total_quantity: 0, total_entries: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const params = () => {
    const qs = new URLSearchParams();
    if (dateFrom) qs.set("date_from", dateFrom);
    if (dateTo) qs.set("date_to", dateTo);
    if (supplierId) qs.set("supplier_id", supplierId);
    if (warehouseId) qs.set("warehouse_id", warehouseId);
    return qs.toString();
  };

  useEffect(() => {
    Promise.all([api.get("/suppliers"), api.get("/warehouses")])
      .then(([s, w]) => { setSuppliers(s.data || []); setWarehouses(w.data || []); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/purchase-summary?${params()}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, supplierId, warehouseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const t = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/purchase-summary/export?${params()}`, {
        credentials: "include", headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error(await res.text());
      const url = window.URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url; a.download = `riepilogo-acquisti_${dateFrom || "inizio"}_${dateTo || "oggi"}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Riepilogo esportato");
    } catch (e) { toast.error(formatApiError(e) || "Errore durante l'export"); }
    finally { setExporting(false); }
  };

  const supplierOpts = useMemo(() => [{ value: "", label: "Tutti i fornitori" }, ...suppliers.map((s) => ({ value: s.id, label: s.name, keywords: [s.name] }))], [suppliers]);
  const warehouseOpts = useMemo(() => [{ value: "", label: "Tutti i magazzini" }, ...warehouses.map((w) => ({ value: w.id, label: w.name, sublabel: w.type === "mobile" ? "Furgone" : "Fisico", keywords: [w.name] }))], [warehouses]);

  return (
    <div>
      <PageHeader
        eyebrow="Approvvigionamento"
        title="Riepilogo Acquisti"
        subtitle="Prodotti entrati nel periodo, raggruppati per prodotto e fornitore (quantità × prezzo unitario)."
        action={
          <button data-testid="ps-export-btn" onClick={exportXlsx} disabled={exporting} className="text-sm px-4 py-2.5 bg-[#002FA7] hover:bg-[#002480] disabled:opacity-60 text-white transition-colors flex items-center gap-2">
            <DownloadSimple size={16} weight="bold" /> <span>{exporting ? "Esporto..." : "Esporta Excel"}</span>
          </button>
        }
      />
      <div className="p-4 md:p-8 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 bg-white border border-neutral-200 p-4">
          <label className="block">
            <span className="label-eyebrow block mb-2">Dal</span>
            <input type="date" data-testid="ps-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 text-sm" />
          </label>
          <label className="block">
            <span className="label-eyebrow block mb-2">Al</span>
            <input type="date" data-testid="ps-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 text-sm" />
          </label>
          <div>
            <span className="label-eyebrow block mb-2">Fornitore</span>
            <SearchableSelect testid="ps-supplier" value={supplierId} options={supplierOpts} onChange={setSupplierId} placeholder="Tutti i fornitori" />
          </div>
          <div>
            <span className="label-eyebrow block mb-2">Magazzino</span>
            <SearchableSelect testid="ps-warehouse" value={warehouseId} options={warehouseOpts} onChange={setWarehouseId} placeholder="Tutti i magazzini" />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Stat testid="ps-total-amount" label="Totale acquistato" value={eur(data.total_amount)} strong />
          <Stat testid="ps-total-qty" label="Quantità totale" value={data.total_quantity.toLocaleString("it-IT")} />
          <Stat testid="ps-total-entries" label="Entrate" value={data.total_entries} />
        </div>

        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="ps-table">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">Fornitore</th>
                <th className="px-4 py-3 label-eyebrow">SKU</th>
                <th className="px-4 py-3 label-eyebrow">Prodotto</th>
                <th className="px-4 py-3 label-eyebrow text-right">Quantità</th>
                <th className="px-4 py-3 label-eyebrow text-right">Prezzo medio</th>
                <th className="px-4 py-3 label-eyebrow text-right">Totale</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-12 text-center text-neutral-500" data-testid="ps-empty">
                  {loading ? "Caricamento..." : "Nessuna entrata nel periodo selezionato."}
                </td></tr>
              ) : data.rows.map((r) => (
                <tr key={`${r.product_id}-${r.supplier_id}`} data-testid={`ps-row-${r.product_id}-${r.supplier_id || "none"}`} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3"><div className="flex items-center gap-1.5"><Truck size={14} className="text-neutral-400 shrink-0" />{r.supplier_name}</div></td>
                  <td className="px-4 py-3 font-mono text-xs">{r.product_sku}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Package size={14} className="text-neutral-400 shrink-0" />
                      <span>{r.product_name}</span>
                      {r.is_serialized === false && <span className="text-[10px] bg-neutral-200 text-neutral-700 px-1.5 py-0.5 uppercase tracking-wider">Bulk</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{r.quantity} <span className="text-neutral-400 text-xs">{r.unit_of_measure}</span></td>
                  <td className="px-4 py-3 text-right font-mono">{eur(r.avg_price)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{eur(r.total)}</td>
                </tr>
              ))}
            </tbody>
            {data.rows.length > 0 && (
              <tfoot className="bg-neutral-50 border-t-2 border-neutral-300">
                <tr className="font-semibold">
                  <td className="px-4 py-3" colSpan="3">Totale periodo</td>
                  <td className="px-4 py-3 text-right font-mono">{data.total_quantity}</td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-right font-mono text-[#002FA7]" data-testid="ps-footer-total">{eur(data.total_amount)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, strong, testid }) => (
  <div className="bg-white border border-neutral-200 p-4">
    <div className="label-eyebrow mb-1">{label}</div>
    <div data-testid={testid} className={`font-mono ${strong ? "text-xl md:text-2xl font-bold text-[#002FA7]" : "text-lg font-semibold"}`}>{value}</div>
  </div>
);
